import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetAdminTestimonialsQuery,
  useDeleteTestimonialMutation,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import Icon from "../components/Icon";
import { createExcerpt } from "../utils/excerpt";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";

const Testimonials = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();

  // RTK Query hooks - use admin endpoint for dashboard
  const {
    data: testimonialsData,
    isLoading: loading,
    error,
  } = useGetAdminTestimonialsQuery();
  const [deleteTestimonialMutation] = useDeleteTestimonialMutation();

  const testimonials = testimonialsData?.testimonials || [];
  const [deleteStatus, setDeleteStatus] = useState("");
  const messageType = useMessageType(deleteStatus);

  const handleDeleteTestimonial = async (id) => {
    if (window.confirm(t("testimonialsDashboard.deleteConfirmMessage"))) {
      try {
        await deleteTestimonialMutation(id).unwrap();
        setDeleteStatus(
          t("testimonialsDashboard.testimonialDeletedSuccessfully")
        );
      } catch (error) {
        console.error("Error deleting testimonial:", error);
        setDeleteStatus(
          error.data?.message ||
            t("testimonialsDashboard.errorDeletingTestimonial")
        );
      }
    }
  };

  const handleAddNew = () => {
    navigate("/dashboard/testimonials/add");
  };

  const handleEdit = (testimonialId) => {
    navigate(`/dashboard/testimonials/edit/${testimonialId}`);
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isHalfStar = rating === i - 0.5;
      const isFullStar = rating >= i;

      stars.push(
        <span
          key={i}
          className="star-container"
          style={{
            position: "relative",
            display: "inline-block",
            marginRight: "2px",
          }}
        >
          {/* Background star (always gray) */}
          <span
            className="dashboard-star"
            style={{
              color: "#666",
              position: "relative",
              zIndex: 1,
            }}
          >
            ★
          </span>

          {/* Foreground star (colored when selected) */}
          <span
            className={`dashboard-star ${
              isFullStar || isHalfStar ? "filled" : ""
            }`}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              zIndex: 2,
              color: isFullStar || isHalfStar ? "#ffd700" : "transparent",
              overflow: isHalfStar ? "hidden" : "visible",
              width: isHalfStar ? "50%" : "100%",
            }}
          >
            ★
          </span>
        </span>
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="dashboard-content">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("testimonialsDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content">
        <div className="page-header">
          <h1>{t("testimonialsDashboard.testimonials")}</h1>
          <button className="btn btn-primary" onClick={handleAddNew}>
            {t("testimonialsDashboard.addNewTestimonial")}
          </button>
        </div>
        <div className="error-container">
          <h3>Error Loading Testimonials</h3>
          <p>{error.message || "Failed to load testimonials"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="services">
      <div className="page-header">
        <div className="header-content">
          <h1>{t("testimonialsDashboard.testimonials")}</h1>
          <p>{t("testimonialsDashboard.manageYourTestimonials")}</p>
        </div>
        <div className="header-actions">
          <button type="button" className="add-btn" onClick={handleAddNew}>
            <Icon name="plus" size="16px" />
            {t("testimonialsDashboard.addNewTestimonial")}
          </button>
        </div>
      </div>

      <StatusMessage
        message={deleteStatus}
        type={messageType}
        show={!!deleteStatus}
        autoClose={messageType === "success"}
        autoCloseDelay={5000}
        onClose={() => setDeleteStatus("")}
      />

      <div className="services-container">
        {testimonials.length > 0 ? (
          <div className="services-table-container">
            <table className="services-table">
              <thead>
                <tr>
                  <th>{t("testimonialsDashboard.image")}</th>
                  <th>{t("testimonialsDashboard.name")}</th>
                  <th>{t("testimonialsDashboard.rating")}</th>
                  <th>{t("testimonialsDashboard.content")}</th>
                  <th>{t("testimonialsDashboard.status")}</th>
                  <th>{t("testimonialsDashboard.created")}</th>
                  <th>{t("testimonialsDashboard.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {testimonials.map((testimonial) => (
                  <tr key={testimonial.id} className="service-row">
                    <td className="image-cell">
                      {testimonial.image_url ? (
                        <img
                          src={testimonial.image_url}
                          alt={testimonial.name}
                          className="service-thumbnail"
                        />
                      ) : (
                        <div className="no-image">
                          <Icon name="user" size="24px" />
                        </div>
                      )}
                    </td>
                    <td className="title-cell">
                      <strong>{testimonial.name}</strong>
                    </td>
                    <td className="category-cell">
                      <div className="star-rating">
                        {renderStars(testimonial.rating)}
                      </div>
                    </td>
                    <td className="content-cell">
                      <div className="content-preview">
                        {createExcerpt(testimonial.content, 15)}
                      </div>
                    </td>
                    <td className="status-cell">
                      <span
                        className={`status-badge ${
                          testimonial.is_active ? "active" : "inactive"
                        }`}
                      >
                        {testimonial.is_active
                          ? t("testimonialsDashboard.active")
                          : t("testimonialsDashboard.inactive")}
                      </span>
                    </td>
                    <td className="date-cell">
                      {new Date(testimonial.created_at).toLocaleDateString()}
                    </td>
                    <td className="actions-cell">
                      <div
                        className="action-buttons"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(testimonial.id)}
                          title={t("testimonialsDashboard.editTestimonial")}
                        >
                          <Icon name="edit" size="14px" />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() =>
                            handleDeleteTestimonial(testimonial.id)
                          }
                          title={t("testimonialsDashboard.deleteTestimonial")}
                        >
                          <Icon name="trash" size="14px" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-services">
            <div className="empty-content">
              <Icon name="quote-left" size="48px" />
              <h3>{t("testimonialsDashboard.noTestimonialsYet")}</h3>
              <p>{t("testimonialsDashboard.getStartedByCreating")}</p>
              <button className="add-first-service-btn" onClick={handleAddNew}>
                <Icon name="plus" size="16px" />
                {t("testimonialsDashboard.addFirstTestimonial")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Testimonials;
